import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

import { BehaviorSubject, Observable, catchError, combineLatest, map, of, switchMap, take } from 'rxjs';

import { CurationAgentsApiService } from '../../../api/agents/curation-agents.api.service';
import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { SourceUsage } from '../../../api/agents/models/curation-agent.api';
import { TakeOverPanelComponent } from '../../../Common/TakeOver/components/TakeOverPanel/take-over-panel.component';
import { AbstractDialogComponent } from '../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { CurationAgentsService } from '../../services/curation-agents.service';
import { AgentUsagePeriodEnum, quotaQueryPeriods } from '../AgentUsageDashboard/agent-usage-dashboard.component';

/** One row of the consumers table: what spent the quota, how much, and which kind of agent it was */
export interface AgentQuotaConsumer {
    name: string;
    usage: number;
    type: string;
}

/**
 * The usage dashboard opened out. Keeps the panel itself on the left, exactly as it renders inline,
 * and adds the per agent breakdown there is no room for in the narrow column.
 */
@Component({
    selector: 'agpr-agent-usage-dialog',
    templateUrl: './agent-usage-dialog.component.html',
    styleUrls: ['./agent-usage-dialog.component.scss'],
    standalone: false
})
export class AgentUsageDialogComponent extends AbstractDialogComponent implements OnInit {

    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverity = messageSeverityEnum;

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    /** The window the panel was on when it was expanded, so the dialog opens on the same one */
    @Input() selectedPeriod: AgentUsagePeriodEnum = AgentUsagePeriodEnum.current;

    /** The window the dialog is on, so the panel it was expanded from can follow it back */
    @Output() periodSelected: EventEmitter<AgentUsagePeriodEnum> = new EventEmitter<AgentUsagePeriodEnum>();

    @ViewChild('takeOverPanel') takeOverPanel: TakeOverPanelComponent;

    hideSlideOverlay: boolean = false;

    consumers$: Observable<AgentQuotaConsumer[]>;
    loading: boolean = true;

    /** The window the panel beside the table is on, which it announces as soon as it loads */
    private readonly period: BehaviorSubject<AgentUsagePeriodEnum> =
        new BehaviorSubject<AgentUsagePeriodEnum>(AgentUsagePeriodEnum.current);

    /** The panel announces its window as soon as it loads, so this only seeds the first read */

    constructor(
        coreDialogService: CoreDialogService,
        private curationAgentsService: CurationAgentsService,
        private curationAgentsApi: CurationAgentsApiService
    ) {
        super(coreDialogService, 'AgentUsageDialog');
    }

    ngOnInit(): void {
        // The quota response counts usage per source; the table reports it per agent, over whatever
        // window the panel beside it is on
        this.consumers$ = combineLatest([
            this.curationAgentsService.agents$,
            this.period.pipe(switchMap(period => this.loadUsage(period)))
        ]).pipe(
            map(([agents, sources]) => {
                const totals = this.totalsByAgent(sources);
                const consumers = agents
                    .filter(agent => totals.has(agent.id))
                    .map(agent => ({
                        name: agent.name,
                        usage: totals.get(agent.id),
                        type: 'AutomationAgents.usage.type.curation'
                    }));

                this.loading = false;

                return consumers.sort((a, b) => b.usage - a.usage);
            })
        );
    }

    /** Follows the panel's period dropdown, so both halves report on the same window */
    periodChanged(period: AgentUsagePeriodEnum): void {
        this.loading = true;
        this.period.next(period);
        this.periodSelected.emit(period);
    }

    /**
     * Per source usage for one window. An account whose pool is younger than a period has no
     * previous one to report, which the endpoint says with a 400 - nothing to show rather than an
     * error, so the table falls through to its own empty state.
     */
    private loadUsage(period: AgentUsagePeriodEnum): Observable<SourceUsage[]> {
        return this.curationAgentsApi.getQuota({ period: quotaQueryPeriods[period] }).pipe(
            take(1),
            map(quota => quota.usage?.sources ?? []),
            catchError(() => of([]))
        );
    }

    /**
     * Runs the panel's own slide-out so the arrow matches the X; close() is a no-op until the panel
     * has finished opening, so fall back to closing outright in that window.
     */
    collapse(): void {
        if (this.takeOverPanel?.isPanelFullyOpened) {
            this.takeOverPanel.close();

            return;
        }

        this.closeDialog();
    }

    /**
     * The takeover only animates itself out - the hosting dialog has to remove itself, otherwise the
     * component stays mounted on the body and the panel reappears on the next change detection pass.
     */
    onVisibleChange(isVisible: boolean): void {
        if (!isVisible) {
            this.closeDialog();
        }
    }

    /** Sources and ad hoc submissions both count against the agent that owns them */
    private totalsByAgent(sources: SourceUsage[]): Map<number, number> {
        const totals = new Map<number, number>();

        sources.forEach(source => totals.set(source.agentId, (totals.get(source.agentId) ?? 0) + source.count));

        return totals;
    }
}
